import React from 'react';
import { FileText, CheckCircle2, XCircle, Scan } from 'lucide-react';

const PACK_ARTWORK_URL = 'https://customer-assets.emergentagent.com/job_32a2f735-7599-41ab-8995-99b8947ff2bc/artifacts/o0eaaxwn_pack%20graphics%20for%20website.svg';

/**
 * AuditDemoMotion — A looping motion-graphic that pitches "an audit in a click".
 * Pure CSS keyframes (timeline below). No audio, no library deps.
 *
 * Timeline (loops every 18s):
 *   0–2.5s   PDF icon flies into the dropzone.
 *   2.5–5s   Dropzone morphs into a pack flat-layout (real artwork SVG).
 *   5–8s     Horizontal scan line sweeps; PDP (left) + BOP (right) bounding-boxes draw in.
 *   8–13s    Right-side checklist streams in (each row pops in 600ms later).
 *   13–15.5s Score gauge fills 0 → 88; "AUDIT COMPLETE" badge pops.
 *   15.5–18s Caption fades in: "Audit in a click." Then everything fades and the loop restarts.
 */
const CHECKLIST = [
  { id: '2.1.1', label: 'Net weight visible', ok: true },
  { id: '2.2.4', label: 'FSSAI logo + Lic #', ok: true },
  { id: '2.3.1', label: 'Veg / Non-veg mark', ok: true },
  { id: '2.4.2', label: 'Best-before format', ok: false },
  { id: '2.5.1', label: 'Allergen warning', ok: true },
  { id: '2.6.3', label: 'Mfg address present', ok: true },
  { id: '2.7.0', label: 'Font size ≥ 1mm', ok: false },
  { id: '2.8.0', label: 'Storage instructions', ok: true },
];

export const AuditDemoMotion = () => {
  return (
    <div className="audit-motion absolute inset-0 grid place-items-center p-6 lg:p-10" data-testid="hero-audit-motion">
      <div className="audit-motion-stage relative w-full max-w-[520px] aspect-[5/4] overflow-hidden">

        {/* ───── 1. PDF DROP ZONE + FLYING PDF ───── */}
        <div className="am-stage am-stage-drop absolute inset-0 grid place-items-center">
          <div className="relative w-44 h-56 border-2 border-dashed border-border grid place-items-center bg-background/50">
            <div className="text-center">
              <FileText size={32} strokeWidth={1.4} className="mx-auto text-muted-foreground" />
              <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Drop PDF</div>
            </div>
            <div className="am-flying-pdf absolute w-12 h-16 bg-gradient-to-br from-purple-500 to-teal-400 shadow-[0_8px_30px_rgba(168,85,247,0.4)] grid place-items-center">
              <FileText size={18} className="text-white" strokeWidth={1.6} />
            </div>
          </div>
        </div>

        {/* ───── 2. PACK ARTWORK + SCAN ───── */}
        <div className="am-stage am-stage-scan absolute inset-0 grid place-items-center">
          <div className="relative w-[440px] h-[320px] bg-white border border-border shadow-[0_20px_60px_-20px_rgba(168,85,247,0.3)] overflow-hidden">
            {/* Real pack flat-layout artwork (folding carton: PDP/BOP/PDP/BOP across 4 panels) */}
            <img
              src={PACK_ARTWORK_URL}
              alt="Pack flat layout"
              className="absolute inset-0 w-full h-full object-contain p-1"
              draggable={false}
            />

            {/* Scan beam */}
            <div className="am-scan-beam absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-purple-400 to-transparent shadow-[0_0_24px_4px_rgba(168,85,247,0.6)]" />

            {/* ── Detection boxes (4 main panels, top:36% bottom:79%) ── */}
            <div className="am-detect am-detect-pdp1 absolute top-[36%] bottom-[21%] left-[7%] w-[21%] border-2 border-purple-600 bg-purple-500/5">
              <div className="absolute -top-[14px] left-0 font-mono text-[8px] font-bold uppercase tracking-widest text-purple-600 bg-white px-1 py-0.5">PDP</div>
            </div>
            <div className="am-detect am-detect-bop1 absolute top-[36%] bottom-[21%] left-[29%] w-[21%] border-2 border-teal-500 bg-teal-500/5">
              <div className="absolute -top-[14px] left-0 font-mono text-[8px] font-bold uppercase tracking-widest text-teal-500 bg-white px-1 py-0.5">BOP</div>
            </div>
            <div className="am-detect am-detect-pdp2 absolute top-[36%] bottom-[21%] left-[51%] w-[21%] border-2 border-purple-600 bg-purple-500/5">
              <div className="absolute -top-[14px] left-0 font-mono text-[8px] font-bold uppercase tracking-widest text-purple-600 bg-white px-1 py-0.5">PDP</div>
            </div>
            <div className="am-detect am-detect-bop2 absolute top-[36%] bottom-[21%] left-[73%] w-[21%] border-2 border-teal-500 bg-teal-500/5">
              <div className="absolute -top-[14px] left-0 font-mono text-[8px] font-bold uppercase tracking-widest text-teal-500 bg-white px-1 py-0.5">BOP</div>
            </div>

            {/* ── Micro-annotations on the LEFT PDP panel (Brand · FSSAI · Net wt) ── */}
            <div className="am-anno am-anno-1 absolute top-[40%] left-[9%]">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                <span className="h-[1px] w-3 bg-purple-600" />
                <span className="font-mono text-[7px] font-semibold uppercase tracking-widest text-purple-700 bg-white px-1 py-px">Brand</span>
              </div>
            </div>
            <div className="am-anno am-anno-2 absolute top-[57%] left-[12%]">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                <span className="h-[1px] w-3 bg-purple-600" />
                <span className="font-mono text-[7px] font-semibold uppercase tracking-widest text-purple-700 bg-white px-1 py-px">FSSAI</span>
              </div>
            </div>
            <div className="am-anno am-anno-3 absolute top-[71%] left-[12%]">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                <span className="h-[1px] w-3 bg-purple-600" />
                <span className="font-mono text-[7px] font-semibold uppercase tracking-widest text-purple-700 bg-white px-1 py-px">Net wt</span>
              </div>
            </div>

            {/* ── Micro-annotations on the LEFT BOP panel (Ingredients · Nutri-table) ── */}
            <div className="am-anno am-anno-4 absolute top-[55%] left-[31%]">
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                <span className="h-[1px] w-3 bg-teal-500" />
                <span className="font-mono text-[7px] font-semibold uppercase tracking-widest text-teal-600 bg-white px-1 py-px">Ingredients</span>
              </div>
            </div>
            <div className="am-anno am-anno-5 absolute top-[44%] left-[41%]">
              <div className="flex items-center gap-1">
                <span className="font-mono text-[7px] font-semibold uppercase tracking-widest text-teal-600 bg-white px-1 py-px">Nutri-table</span>
                <span className="h-[1px] w-3 bg-teal-500" />
                <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* ───── 3. CHECKLIST + SCORE ───── */}
        <div className="am-stage am-stage-score absolute inset-0 p-6 lg:p-8 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Scan size={11} className="text-accent" strokeWidth={1.8} />
              60+ checks running
            </div>
            <div className="font-mono text-[10px] text-muted-foreground">artwork.pdf · 3.2 MB</div>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-6 flex-1">
            {/* Checklist (left) */}
            <ul className="space-y-1.5 self-center">
              {CHECKLIST.map((c, i) => (
                <li
                  key={c.id}
                  className="am-check-row flex items-center gap-2 text-[11px]"
                  style={{ animationDelay: `${0.4 + i * 0.6}s` }}
                >
                  {c.ok
                    ? <CheckCircle2 size={12} className="text-teal-400 shrink-0" strokeWidth={2} />
                    : <XCircle size={12} className="text-red-400 shrink-0" strokeWidth={2} />}
                  <span className="font-mono text-muted-foreground">{c.id}</span>
                  <span className={c.ok ? '' : 'text-red-400'}>{c.label}</span>
                </li>
              ))}
            </ul>

            {/* Score (right) */}
            <div className="am-score-block self-center text-center">
              <svg className="am-gauge" width="120" height="120" viewBox="0 0 120 120">
                <defs>
                  <linearGradient id="ptg" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#a855f7" />
                    <stop offset="100%" stopColor="#14b8a6" />
                  </linearGradient>
                </defs>
                <circle cx="60" cy="60" r="50" fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="8" />
                <circle
                  className="am-gauge-ring"
                  cx="60" cy="60" r="50"
                  fill="none"
                  stroke="url(#ptg)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="am-score-num font-mono text-3xl font-medium mt-2">88</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Compliance</div>
            </div>
          </div>
        </div>

        {/* ───── 4. FINAL CAPTION ───── */}
        <div className="am-stage am-stage-caption absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-medium tracking-tight">
              An audit in a <span className="text-gradient-pt">click.</span>
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              PackCheck by Therefore
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AuditDemoMotion;
